import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { Room, TableTemplate } from '@shared/types';
import { Header } from '../components/layout/Header';
import { TemplateManager } from '../components/admin/TemplateManager';
import { ScheduleManager } from '../components/admin/ScheduleManager';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorBoundary } from '../components/common/ErrorBoundary';

export function SchedulerPage() {
  const { userProfile } = useAuth();
  const [searchParams] = useSearchParams();
  const roomId = searchParams.get('roomId') || userProfile?.roomId || '';
  const [room, setRoom] = useState<Room | null>(null);
  const [loading, setLoading] = useState(true);
  const [templateToSchedule, setTemplateToSchedule] = useState<TableTemplate | null>(null);

  useEffect(() => {
    if (!roomId) {
      setLoading(false);
      return;
    }

    const fetchRoom = async () => {
      try {
        const roomDoc = await getDoc(doc(db, 'rooms', roomId));
        if (roomDoc.exists()) {
          setRoom({ id: roomDoc.id, ...roomDoc.data() } as Room);
        }
      } catch (err) {
        console.error('Failed to fetch room:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchRoom();
  }, [roomId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!roomId) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">No Room Selected</h1>
          <p className="text-gray-600">Please access the scheduler from the Admin Dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gray-50">
        <Header userProfile={userProfile} />

        <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center space-x-3 mb-2">
                <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <h1 className="text-3xl font-bold text-gray-900">
                  {room?.name || 'Poker Room'} — Table Scheduler
                </h1>
              </div>
              <p className="mt-2 text-gray-600">
                Create templates and schedule tables to be automatically created at a specific date and time
              </p>
            </div>

            {/* Template Management */}
            <div className="mb-8">
              <TemplateManager
                roomId={roomId}
                onScheduleTemplate={setTemplateToSchedule}
              />
            </div>

            {/* Schedule Management */}
            <div>
              <ScheduleManager
                roomId={roomId}
                templateToSchedule={templateToSchedule}
                onClearTemplate={() => setTemplateToSchedule(null)}
              />
            </div>
          </div>
        </main>
      </div>
    </ErrorBoundary>
  );
}
